import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { LuX } from "react-icons/lu";
import {
  parseAdditionalQuotationEmails,
  validateAdditionalQuotationEmails,
} from "utils/quotationCustomerValidation";

export default function QuotationRecipientField({
  id,
  primaryEmail = "",
  initialRecipients,
  savedTitle = "Saved recipient",
  label = "To",
  loading = false,
  disabled = false,
  primaryError = "",
  additionalError = "",
  onChange,
  onError,
}) {
  const [recipients, setRecipients] = useState(() => {
    if (Array.isArray(initialRecipients)) return initialRecipients;
    if (typeof initialRecipients === "string" && initialRecipients.trim()) {
      return parseAdditionalQuotationEmails(initialRecipients);
    }
    return [];
  });
  const [entry, setEntry] = useState("");
  const inputRef = useRef(null);
  const error = primaryError || additionalError;

  useEffect(() => {
    if (initialRecipients !== undefined) {
      const parsed = Array.isArray(initialRecipients)
        ? initialRecipients
        : typeof initialRecipients === "string" && initialRecipients.trim()
        ? parseAdditionalQuotationEmails(initialRecipients)
        : [];
      setRecipients(parsed);
    }
  }, [initialRecipients]);

  const update = (emails, draft) => {
    setRecipients(emails);
    setEntry(draft);
    onChange([...emails, draft].filter(Boolean).join(", "), [...emails, draft].filter(Boolean));
  };

  const addRecipients = () => {
    if (!entry.trim()) return;
    const validationError = validateAdditionalQuotationEmails(entry);
    onError(validationError);
    if (validationError) return;
    const seen = new Set(
      [primaryEmail, ...recipients]
        .filter(Boolean)
        .map((email) => email.toLowerCase())
    );
    const additions = parseAdditionalQuotationEmails(entry).filter((email) => {
      const key = email.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    update([...recipients, ...additions], "");
  };

  return (
    <div className="quotation-dialog-field">
      <label htmlFor={id}>{label}</label>
      <div
        className={`quotation-recipient-box${error ? " has-error" : ""}`}
        aria-busy={loading}
      >
        {primaryEmail ? (
          <span
            className="quotation-recipient-chip quotation-recipient-chip--saved"
            title={savedTitle}
          >
            <span>{primaryEmail}</span>
          </span>
        ) : (
          !recipients.length && (
            <span className="quotation-recipient-loading">
              {loading
                ? "Loading recipient email…"
                : "Customer email unavailable"}
            </span>
          )
        )}
        {recipients.map((email) => (
          <span className="quotation-recipient-chip" key={email.toLowerCase()}>
            <span>{email}</span>
            <button
              type="button"
              aria-label={`Remove ${email}`}
              disabled={disabled || loading}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                update(
                  recipients.filter((recipient) => recipient !== email),
                  entry
                );
                onError("");
                inputRef.current?.focus();
              }}
            >
              <LuX aria-hidden="true" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          className="quotation-recipient-entry"
          type="text"
          inputMode="email"
          autoComplete="off"
          value={entry}
          disabled={disabled || loading}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
          placeholder={
            recipients.length || primaryEmail
              ? "Add another email…"
              : "Add an email…"
          }
          onChange={(event) => {
            update(recipients, event.target.value);
            onError("");
          }}
          onBlur={addRecipients}
          onKeyDown={(event) => {
            if (event.nativeEvent.isComposing) return;
            if (["Enter", ",", ";", "Tab"].includes(event.key)) {
              if (event.key !== "Tab") event.preventDefault();
              if (entry.trim()) addRecipients();
            }
          }}
        />
      </div>
      {error && (
        <span id={`${id}-error`} className="quotation-field-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

QuotationRecipientField.propTypes = {
  id: PropTypes.string.isRequired,
  primaryEmail: PropTypes.string,
  initialRecipients: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]),
  savedTitle: PropTypes.string,
  label: PropTypes.string,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  primaryError: PropTypes.string,
  additionalError: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};
